import { toast } from "react-toastify";
import {
  BinLevel,
  CorridorLevel,
  DepotLevel,
  PartialOrderData,
  RackLevel,
  SectorLevel,
} from "../../management-pages/orders/location-table/model/location-response.model";
import moment from "moment/moment";
import ExcelJS from "exceljs";
import Cell from "exceljs/index";
import WorkSheet from "exceljs/index";
import { OrderModel } from "./models/order/order-item.model";
import { MessageSections } from "../components/message/message";
import { supportedLanguages } from "../../constants";
import { RateResponseModel } from "./models/api/rate-response.model";
import { changeState, getOrders } from "../../redux/slices/orders-slice";
import { Dispatch } from "react";
import { NavigateFunction } from "react-router-dom";
import { User } from "./models/user/user";
import { FileModel } from "./models/FileModel";

export function getMessage(
  labelKey: string,
  section: MessageSections,
  messageCache: any,
  languageId: number,
): string {
  return messageCache[section] && messageCache[section][labelKey]
    ? messageCache[section][labelKey].name
    : messageCache[MessageSections.MAIN] &&
        messageCache[MessageSections.MAIN][labelKey]
      ? messageCache[MessageSections.MAIN][labelKey].name
      : supportedLanguages[languageId - 1] + "_" + labelKey;
}

export function numberWithCommas(x: number) {
  return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
}

export function initToast(message: string, type: string, duration = 10000) {
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  toast[type](message, {
    position: "top-right",
    autoClose: duration,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
  });
}

export function onLogoClick(
  isAdmin: boolean,
  isWarehouseWorker: boolean,
  isLocalWarehouseWorker: boolean,
  currentUser: User | undefined,
  dispatch: Dispatch<any>,
  navigate: NavigateFunction,
) {
  const { pathname } = window.location;
  if (localStorage.getItem("access_token") && currentUser) {
    if (isWarehouseWorker) {
      navigate("management/warehouse-orders/my");
    } else if (isAdmin) {
      navigate("management/customers");
    } else if (isLocalWarehouseWorker) {
      navigate("management/local-warehouse-orders");
    } else if(pathname === 'login') {
      navigate("../orders/my");
    } else {
      dispatch(changeState(1));
      dispatch(getOrders({ page: 0 }));

      navigate("../orders/my");
    }
  } else {
    navigate("/");
  }
}

export function getLocationName(
  location: DepotLevel | SectorLevel | CorridorLevel | RackLevel | BinLevel,
) {
  if (location.hasOwnProperty("depot")) {
    return (location as DepotLevel).depot;
  }
  if (location.hasOwnProperty("sector")) {
    return (location as SectorLevel).sector;
  }
  if (location.hasOwnProperty("corridor")) {
    return (location as CorridorLevel).corridor;
  }
  if (location.hasOwnProperty("rack")) {
    return (location as RackLevel).rack;
  }
  if (location.hasOwnProperty("bin")) {
    return (location as BinLevel).bin;
  }

  return "";
}

function getLevelWeight(orders: PartialOrderData[] | undefined) {
  return orders
    ? orders.reduce((previousValue, currentValue) => {
        return previousValue + currentValue.weight;
      }, 0)
    : 0;
}

function getLevelCount(orders: PartialOrderData[] | undefined) {
  return orders ? orders.length : 0;
}

function convertGivenCurrencyToAMD(
  currency: string | undefined,
  value: number | null,
  rate: string | undefined,
) {
  if (currency?.toLowerCase().trim() === "amd") {
    return value;
  } else {
    if (rate) {
      return (value || 0) * +rate;
    }
  }
  return 0.0;
}

function getLevelPrice(orders: PartialOrderData[] | undefined) {
  return orders
    ? orders.reduce((previousValue, currentValue) => {
        return previousValue + currentValue.shippingAmount;
      }, 0)
    : 0;
}

function recursivelyIterate(
  location: DepotLevel | SectorLevel | CorridorLevel | RackLevel | BinLevel,
  initialCount: number,
  // eslint-disable-next-line @typescript-eslint/ban-types
  accFunction: Function,
): number {
  const count = initialCount + accFunction(location.orders);

  if (location.hasOwnProperty("depot")) {
    return (location as DepotLevel).sectors.reduce(
      (previousValue, currentValue) => {
        return recursivelyIterate(currentValue, previousValue, accFunction);
      },
      count,
    );
  }
  if (location.hasOwnProperty("sector")) {
    return (location as SectorLevel).corridors.reduce(
      (previousValue, currentValue) => {
        return recursivelyIterate(currentValue, previousValue, accFunction);
      },
      count,
    );
  }
  if (location.hasOwnProperty("corridor")) {
    return (location as CorridorLevel).racks.reduce(
      (previousValue, currentValue) => {
        return recursivelyIterate(currentValue, previousValue, accFunction);
      },
      count,
    );
  }
  if (location.hasOwnProperty("rack")) {
    return (location as RackLevel).bins.reduce(
      (previousValue, currentValue) => {
        return recursivelyIterate(currentValue, previousValue, accFunction);
      },
      count,
    );
  }

  return count;
}

export function getTotalLocationWeight(
  location: DepotLevel | SectorLevel | CorridorLevel | RackLevel | BinLevel,
  initialCount: number,
): number {
  return recursivelyIterate(location, initialCount, getLevelWeight);
}

export function getTotalLocationCount(
  location: DepotLevel | SectorLevel | CorridorLevel | RackLevel | BinLevel,
  initialCount: number,
): number {
  return recursivelyIterate(location, initialCount, getLevelCount);
}

export function getTotalLocationPrice(
  location: DepotLevel | SectorLevel | CorridorLevel | RackLevel | BinLevel,
  initialCount: number,
): number {
  return recursivelyIterate(location, initialCount, getLevelPrice);
}

function getOrderIds(orders: PartialOrderData[] | undefined) {
  return orders ? orders.map((partialOrder) => partialOrder.orderId) : [];
}

export function getLocationOrderIds(
  location: DepotLevel | SectorLevel | CorridorLevel | RackLevel | BinLevel,
  orderIds: Set<number>,
): number[] {
  getOrderIds(location.orders).forEach((id) => orderIds.add(id));

  if (location.hasOwnProperty("depot")) {
    (location as DepotLevel).sectors.forEach((sector) => {
      getLocationOrderIds(sector, orderIds);
    });
  }
  if (location.hasOwnProperty("sector")) {
    (location as SectorLevel).corridors.forEach((corridor) => {
      getLocationOrderIds(corridor, orderIds);
    });
  }
  if (location.hasOwnProperty("corridor")) {
    (location as CorridorLevel).racks.forEach((rack) => {
      getLocationOrderIds(rack, orderIds);
    });
  }
  if (location.hasOwnProperty("rack")) {
    (location as RackLevel).bins.forEach((bin) => {
      getLocationOrderIds(bin, orderIds);
    });
  }

  return Array.from(orderIds);
}

export async function exportToExcel(
  fileName: string,
  orders: OrderModel[] | Map<number, OrderModel>,
  currentRate: RateResponseModel,
) {
  const currentDate = moment().format("DD-MM-YYYY");
  const currentDateTime = moment().format("DD-MM-YYYY-HH:mm");
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet(currentDate);
  appendHeader(worksheet);
  appendFirstSubHeader(worksheet);
  appendSecondSubHeader(worksheet);
  mergeNecessaryCells(worksheet);

  if (orders) {
    let totalProductsCount = 0;
    let totalNetWeight = 0;
    let totalGrossWeight = 0;
    let totalSumAMD = 0;
    let totalNotPaid = 0;
    let totalPaidByBalance = 0;
    let totalInvoiceValue = 0;
    let rowNumber = 0;

    orders.forEach((order) => {
      totalProductsCount += order?.products?.length || 0;
      totalNetWeight +=
        (order?.weight || 0) + ((order?.weight || 0) * 3) / 100 || 0;
      totalGrossWeight += order?.weight || 0;
      totalSumAMD += order?.paymentInfo?.shippingAmount || 0;
      const currency = order?.products[0]?.currency?.name["4"]
        ?.toLowerCase()
        .trim();
      const rate = currentRate?.Rates?.ExchangeRate?.find(
        (rate) => rate.ISO.toLowerCase() === currency,
      )?.Rate;
      totalInvoiceValue +=
        convertGivenCurrencyToAMD(
          order?.products[0]?.currency?.name["4"],
          order?.products[0]?.price,
          rate,
        ) || 0;
      totalNotPaid +=
        order?.paymentStatus?.paymentStatusId === 2
          ? 0
          : order?.paymentInfo?.shippingAmount || 0;
      totalPaidByBalance +=
        order?.paymentStatus?.paymentStatusId === 2
          ? order?.paymentInfo?.shippingAmount || 0
          : 0;
      const rowData = generateOrderDataToExport(order, rate, ++rowNumber);
      worksheet.addRow(rowData);
    });
    worksheet.addRow(
      getTotalRow(
        totalProductsCount,
        totalNetWeight,
        totalGrossWeight,
        totalSumAMD,
        totalNotPaid,
        totalPaidByBalance,
        totalInvoiceValue,
      ),
    );
  }

  appendStyles(worksheet);

  // Exporting the workbook
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `${fileName}-${currentDateTime}.xlsx`;
  a.click();
  URL.revokeObjectURL(url);
}

function appendHeader(workSheet: WorkSheet) {
  // Define the columns
  workSheet.columns = [
    { header: "NADAWCA / SENDER", key: "sender", width: 25 },
    { header: "", key: "col1", width: 20 },
    { header: "", key: "col2", width: 20 },
    { header: "", key: "col3", width: 20 },
    { header: "", key: "col4", width: 20 },
    { header: "", key: "col5", width: 20 },
    { header: "", key: "col6", width: 20 },
    { header: "", key: "col7", width: 20 },
    { header: "ODBiorca / RECEIVER", key: "col8", width: 20 },
    { header: "", key: "col9", width: 20 },
    { header: "", key: "col10", width: 20 },
    { header: "", key: "col11", width: 20 },
    { header: "", key: "col12", width: 25 },
    { header: "", key: "col13", width: 25 },
    { header: "", key: "col14", width: 20 },
    { header: "", key: "col15", width: 20 },
    { header: "", key: "col16", width: 20 },
    { header: "", key: "col17", width: 20 },
    { header: "", key: "col18", width: 20 },
    { header: "", key: "col19", width: 20 },
    { header: "", key: "col20", width: 20 },
    { header: "", key: "col21", width: 20 },
    {
      header: "SZCZEGÓŁY PRZESYŁKI / SHIPMENT DETAILS",
      key: "col22",
      width: 30,
    },
    { header: "", key: "col23", width: 20 },
    { header: "", key: "col24", width: 20 },
    { header: "", key: "col25", width: 20 },
    { header: "", key: "col26", width: 20 },
    { header: "", key: "col27", width: 20 },
    { header: "", key: "col28", width: 30 },
    { header: "", key: "col29", width: 20 },
    { header: "", key: "col30", width: 20 },
    { header: "", key: "col31", width: 20 },
    { header: " Shipment Payment Details", key: "col32", width: 20 },
    { header: "", key: "col33", width: 20 },
    { header: "", key: "col34", width: 20 },
    { header: "", key: "col35", width: 20 },
    { header: "", key: "col36", width: 20 },
    { header: "Delivery list", key: "col37", width: 20 },
    { header: "", key: "col38", width: 20 },
    { header: "", key: "col39", width: 20 },
    { header: "", key: "col40", width: 20 },
    { header: "", key: "col41", width: 20 },
    { header: "", key: "col42", width: 20 },
    { header: "", key: "col43", width: 20 },
    { header: "Delivery Charges (amd)", key: "col44", width: 20 },
    { header: "", key: "col45", width: 20 },
    { header: "", key: "col46", width: 20 },
    { header: "", key: "col47", width: 20 },
    { header: "", key: "col48", width: 20 },
    { header: "Tracking Status", key: "col49", width: 20 },
    { header: "CREATION DATE", key: "col50", width: 20 },
    { header: "Last Update", key: "col51", width: 20 },
    { header: "", key: "col52", width: 20 },
  ];
}

function appendFirstSubHeader(workSheet: WorkSheet) {
  // Define the first sub header rows
  workSheet.addRow({
    sender: "Barekam Magazyn ID",
    col1: "Lp.",
    col2: "SKLEP",
    col3: "S.Telefon",
    col4: "S.Kraj",
    col5: "S.Miasto",
    col6: "S.Adres",
    col7: "S.Kod pocztowy",
    col8: "Main User",
    col9: "Barekam ID",
    col10: "",
    col11: "Imię",
    col12: "Nazwisko",
    col13: "PASSPORT DETAILS",
    col14: "",
    col15: "",
    col16: "A.Telefon",
    col17: "A.Kraj",
    col18: "A.Miasto",
    col19: "",
    col20: "A.Adres",
    col21: "A.Kod pocztowy",
    col22: "Nr. faktury",
    col23: "List przewozowy",
    col24: "Ilosc",
    col25: "JM",
    col26: "Zawartosc paczki",
    col27: "Category Name",
    col28: "HS codes",
    col29: "Wartosc faktury (amd)",
    col30: "Waga netto (kg)",
    col31: "Waga brutto (kg)",
    col32: "discount",
    col33: "",
    col34: "",
    col35: "Postal Charges (amd)",
    col36: "status",
    col37: "REGION",
    col38: "",
    col39: "ADDRESS",
    col40: "post code",
    col41: "Reciver",
    col42: "",
    col43: "",
    col44: "discount",
    col45: "",
    col46: "Payment details",
    col47: "",
    col48: "",
    col49: "",
    col50: "",
    col51: "User",
    col52: "Time/Date",
  });
}

function appendSecondSubHeader(workSheet: WorkSheet) {
  // Define the second sub header rows
  workSheet.addRow({
    sender: "Barekam Warehouse ID",
    col1: "No.",
    col2: "SHOP",
    col3: "S.Telephone",
    col4: "S.Country",
    col5: "S.City",
    col6: "S.Address",
    col7: "S.Post Code",
    col8: "",
    col9: "By Air",
    col10: "By Land",
    col11: "Name",
    col12: "Surname",
    col13: "SERIES AND NUMBER",
    col14: "EXPIRATION DATE",
    col15: "Attached file",
    col16: "A.Telephone",
    col17: "A.Country",
    col18: "A.City",
    col19: "Region",
    col20: "A.Address",
    col21: "A.Post Code",
    col22: "No. Invoice",
    col23: "WayBill/ tracking number",
    col24: "Quantity",
    col25: "U.O.M. ",
    col26: "Description",
    col27: "",
    col28: "",
    col29: "Value of invoice (amd)",
    col30: "Net weight",
    col31: "Gross weight",
    col32: "%",
    col33: "SUM AMD",
    col34: "Not Paid",
    col35: "Paid",
    col36: "",
    col37: "REGION",
    col38: "City",
    col39: "",
    col40: "",
    col41: "Name",
    col42: "Surname",
    col43: "Attached file",
    col44: "%",
    col45: "SUM AMD",
    col46: "Not Paid",
    col47: "Paid",
    col48: "status",
    col49: "",
    col50: "",
    col51: "",
    col52: "",
  });
}

function mergeNecessaryCells(workSheet: WorkSheet) {
  // Merge cells and apply styles for headers
  workSheet.mergeCells(1, 1, 0, 8);
  workSheet.mergeCells(1, 9, 0, 22);
  workSheet.mergeCells(1, 23, 0, 32);
  workSheet.mergeCells(1, 33, 0, 37);
  workSheet.mergeCells(1, 38, 0, 44);
  workSheet.mergeCells(1, 45, 0, 49);
  workSheet.mergeCells(1, 50, 3, 50);
  workSheet.mergeCells(1, 51, 3, 51);
  workSheet.mergeCells(1, 52, 0, 53);
  workSheet.mergeCells(2, 9, 3, 9);
  workSheet.mergeCells(2, 10, 2, 11);
  workSheet.mergeCells(2, 14, 2, 16);
  workSheet.mergeCells(2, 28, 3, 28);
  workSheet.mergeCells(2, 29, 3, 29);
  workSheet.mergeCells(2, 33, 2, 34);
  workSheet.mergeCells(2, 37, 3, 37);
  workSheet.mergeCells(2, 40, 3, 40);
  workSheet.mergeCells(2, 41, 3, 41);
  workSheet.mergeCells(2, 42, 2, 44);
  workSheet.mergeCells(2, 45, 2, 46);
  workSheet.mergeCells(2, 47, 2, 48);
  workSheet.mergeCells(2, 52, 3, 52);
  workSheet.mergeCells(2, 53, 3, 53);
}

function appendStyles(workSheet: WorkSheet) {
  // Apply styles (fonts, sizes, colors)
  for (let index = 1; index < 4; index++) {
    workSheet.getRow(index).font = {
      name: "Arial",
      size: 12,
      bold: true,
      color: { argb: "FFFFFF" },
    };
    workSheet.getRow(index).height = 30;
    workSheet.getRow(index).alignment = {
      vertical: "middle",
      horizontal: "center",
    };
    workSheet.getRow(index).border = {
      top: { style: "thin", color: { argb: "000000" } },
      left: { style: "thin", color: { argb: "000000" } },
      bottom: { style: "thin", color: { argb: "000000" } },
      right: { style: "thin", color: { argb: "000000" } },
    };
    if (index === 1) {
      workSheet.getRow(index).getCell(1).fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "90e4e4" },
      };
      workSheet.getRow(index).getCell(9).fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "96e9a0" },
      };
      workSheet.getRow(index).getCell(23).fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "0cc494" },
      };
      workSheet.getRow(index).getCell(33).fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "e6b9b8" },
      };
      workSheet.getRow(index).getCell(40).fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "d0c4dc" },
      };
      workSheet.getRow(index).getCell(45).fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "e0e4bc" },
      };
      workSheet.getRow(index).getCell(52).fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "408494" },
      };
    } else if (index === 2) {
      workSheet.getRow(index).fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "0ca4cc" },
      };
      workSheet.getRow(index).getCell(1).fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "d8d4cc" },
      };
    } else if (index === 3) {
      workSheet.getRow(3).fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "0ca4cc" },
      };
      workSheet.getRow(3).getCell(1).fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "d8d4cc" },
      };
    }
  }

  workSheet.getRow(1).getCell(50).fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "b8b4ac" },
  };
  workSheet.getRow(1).getCell(51).fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "e09494" },
  };
  for (let index = 4; index < workSheet.rowCount; index++) {
    workSheet.getRow(index).eachCell((cell: Cell, cellNumber: number) => {
      if (cell.value) {
        if (cell.value == "Paid") {
          workSheet.getRow(index).getCell(cellNumber).fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "94cc50" },
          };
        } else if (cell.value == "Unpaid") {
          workSheet.getRow(index).getCell(cellNumber).fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "ff0404" },
          };
        }
      }
    });
  }
  workSheet.lastRow.eachCell((cell: Cell, cellNumber: number) => {
    if (cell.value && cell.value != "Total") {
      workSheet.lastRow.getCell(cellNumber).fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: "fffc04" },
      };
    }
  });
}

function generateOrderDataToExport(
  order: OrderModel,
  rate: string | undefined,
  rowNumber: number,
) {
  return {
    sender: order?.shippingInfo?.warehouse?.name || "",
    col1: rowNumber || "",
    col2: order?.products[0]?.store
      ? order?.products[0].store?.name[4]
      : order?.products[0].storeName || "",
    col3: order?.shippingInfo?.warehouse?.phone || "",
    col4: order?.shippingInfo?.country?.name[4] || "",
    col5: order?.shippingInfo?.warehouse?.city || "",
    col6: order?.shippingInfo?.warehouse?.addressLine1 || "",
    col7: order?.shippingInfo?.warehouse?.zipCode || "",
    col8:
      `${order?.recipient?.relatedUser?.firstName || ""} ${order?.recipient?.relatedUser?.lastName || ""}` ||
      "",
    col9: order?.recipient?.relatedUser?.addressLine2ForAir || "",
    col10: order?.recipient?.relatedUser?.addressLine2ForLand || "",
    col11: order?.recipient?.firstName || "",
    col12: order?.recipient?.lastName || "",
    col13: order?.recipient?.passportOrIdSerialNumber || "",
    col14:
      moment(order?.recipient?.documentExpirationDate).format("DD/MM/YYYY") ||
      "",
    col15: (order?.recipient?.document as FileModel)?.fileName || "",
    col16: order?.recipient?.phoneNumber || "",
    col17: order?.recipient?.country?.name || "",
    col18: order?.recipient?.city?.name || "",
    col19: order?.recipient?.region?.name || "",
    col20: order?.recipient?.address || "",
    col21: order?.recipient?.zipCode || "",
    col22: order?.internalTrackingNumber || "",
    col23: order?.internalTrackingNumber || "",
    col24: order?.products?.length || "",
    col25: "pcs",
    col26: order?.products[0]?.description || "",
    col27: order?.products[0]?.category?.name[4] || "",
    col28: order?.products[0]?.category?.code || "",
    col29:
      convertGivenCurrencyToAMD(
        order?.products[0]?.currency?.name["4"],
        order?.products[0]?.price,
        rate,
      ) || "",
    col30: (order?.weight || 0) + ((order?.weight || 0) * 3) / 100 || "",
    col31: order?.weight || "",
    col32: order?.paymentInfo?.appliedDiscountPercent || "",
    col33: order?.paymentInfo?.appliedDiscount || "",
    col34:
      order?.paymentStatus?.paymentStatusId !== 2
        ? (order?.paymentInfo?.shippingAmount || 0) -
          (order?.paymentInfo?.appliedDiscount || 0)
        : 0,
    col35:
      order?.paymentStatus?.paymentStatusId === 2
        ? (order?.paymentInfo?.shippingAmount || 0) -
          (order?.paymentInfo?.appliedDiscount || 0)
        : 0,
    col36: order?.paymentStatus?.paymentStatusId === 2 ? "Paid" : "Unpaid",
    col37: order?.shippingInfo?.destinationWarehouse?.state || "",
    col38: order?.shippingInfo?.destinationWarehouse?.city || "",
    col39: order?.shippingInfo?.destinationWarehouse?.addressLine1 || "",
    col40: order?.shippingInfo?.destinationWarehouse?.zipCode || "",
    col41: order?.recipient?.firstName || "",
    col42: order?.recipient?.lastName || "",
    col43: (order?.recipient?.document as FileModel)?.fileName || "",
    col44: order?.paymentInfo?.appliedDiscountPercent || "",
    col45: order?.paymentInfo?.appliedDiscount || "",
    col46:
      order?.paymentStatus?.paymentStatusId !== 2
        ? (order?.paymentInfo?.shippingAmount || 0) -
          (order?.paymentInfo?.appliedDiscount || 0)
        : 0,
    col47:
      order?.paymentStatus?.paymentStatusId === 2
        ? (order?.paymentInfo?.shippingAmount || 0) -
          (order?.paymentInfo?.appliedDiscount || 0)
        : 0,
    col48: order?.paymentStatus?.paymentStatusId === 2 ? "Paid" : "Unpaid",
    col49: order?.trackRecord[0]?.state?.name || "",
    col50: moment(order?.enteredDate).format("DD/MM/YYYY") || "",
    col51: `${order?.updatedBy?.firstName} ${order?.updatedBy?.lastName}` || "",
    col52: moment(order?.updatedOn).format("DD/MM/YYYY") || "",
  };
}

function getTotalRow(
  totalProductsCount: number,
  totalNetWeight: number,
  totalGrossWeight: number,
  totalSumAMD: number,
  totalNotPaid: number,
  totalPaidByBalance: number,
  totalInvoiceValue: number,
) {
  return {
    sender: "",
    col1: "Total",
    col2: "",
    col3: "",
    col4: "",
    col5: "",
    col6: "",
    col7: "",
    col8: "",
    col9: "",
    col10: "",
    col11: "",
    col12: "",
    col13: "",
    col14: "",
    col15: "",
    col16: "",
    col17: "",
    col18: "",
    col19: "",
    col20: "",
    col21: "",
    col22: "",
    col23: "",
    col24: totalProductsCount || "",
    col25: "",
    col26: "",
    col27: "",
    col28: "",
    col29: totalInvoiceValue || "",
    col30: totalNetWeight || "",
    col31: totalGrossWeight || "",
    col32: "",
    col33: "",
    col34: totalNotPaid || "",
    col35: totalPaidByBalance || "",
    col36: "",
    col37: "",
    col38: "",
    col39: "",
    col40: "",
    col41: "",
    col42: "",
    col43: "",
    col44: "",
    col45: "",
    col46: totalNotPaid || "",
    col47: totalPaidByBalance || "",
    col48: "",
    col49: "",
    col50: "",
    col51: "",
    col52: "",
    col53: "",
  };
}
